import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    InputAdornment,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { EligibleSponsor } from '../../../../networking/salesFunnel.types';
import {
    adminCreateSalesFunnelProspect,
    AdminCreateProspectPayload,
} from '../../../../networking/salesFunnel.api';

const FACILITY_TYPES = [
    'University',
    'High School',
    'Club',
    'Gym',
    'Pro Team',
    'Academy',
    'Training Center',
    'Other',
];

interface AdminAddProspectDialogProps {
    open: boolean;
    onClose: () => void;
    sponsors: EligibleSponsor[];
    defaultOrgUuid?: string | null;
    onCreated: () => void;
}

const emptyValues = (orgUuid = ''): AdminCreateProspectPayload => ({
    organization_uuid: orgUuid,
    prospect_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    facility_type: '',
    facility_size: '',
    athlete_profile: '',
    athlete_count: undefined,
    estimated_value: undefined,
    notes: '',
});

const validationSchema = Yup.object({
    organization_uuid: Yup.string().required('Please select a sponsor'),
    prospect_name: Yup.string().required('Prospect name is required').max(255),
    contact_name: Yup.string().nullable().max(255),
    contact_email: Yup.string().nullable().email('Invalid email'),
    contact_phone: Yup.string().nullable().max(50),
    facility_type: Yup.string().nullable().max(100),
    facility_size: Yup.string().nullable().max(100),
    athlete_profile: Yup.string().nullable().max(255),
    athlete_count: Yup.number().nullable().min(0).integer(),
    estimated_value: Yup.number().nullable().min(0),
});

const AdminAddProspectDialog: React.FC<AdminAddProspectDialogProps> = ({
    open,
    onClose,
    sponsors,
    defaultOrgUuid,
    onCreated,
}) => {
    const [successCount, setSuccessCount] = useState(0);
    const [lastProspectName, setLastProspectName] = useState('');
    const [keepSponsor, setKeepSponsor] = useState<string>('');
    const prospectNameRef = useRef<HTMLInputElement>(null);

    const formik = useFormik<AdminCreateProspectPayload>({
        initialValues: emptyValues(defaultOrgUuid ?? ''),
        enableReinitialize: false,
        validationSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                await adminCreateSalesFunnelProspect(values);
                setLastProspectName(values.prospect_name);
                setKeepSponsor(values.organization_uuid);
                setSuccessCount((c) => c + 1);
                onCreated();
                // Reset form but keep the sponsor selected for quick batch entry
                resetForm({ values: emptyValues(values.organization_uuid) });
                setTimeout(() => prospectNameRef.current?.focus(), 100);
            } catch (err) {
                console.error('Failed to create prospect', err);
            } finally {
                setSubmitting(false);
            }
        },
    });

    // When dialog opens, set defaultOrgUuid if provided
    useEffect(() => {
        if (open) {
            setSuccessCount(0);
            setLastProspectName('');
            const orgUuid = defaultOrgUuid ?? keepSponsor ?? '';
            formik.resetForm({ values: emptyValues(orgUuid) });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, defaultOrgUuid]);

    const selectedSponsor =
        sponsors.find((s) => s.organization_uuid === formik.values.organization_uuid) ?? null;

    const handleClose = () => {
        setSuccessCount(0);
        setLastProspectName('');
        formik.resetForm({ values: emptyValues() });
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <form onSubmit={formik.handleSubmit}>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <PersonAddIcon color="primary" />
                        <Box>
                            <Typography variant="h6" component="span">
                                Add Prospect for Sponsor
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Prospect will be assigned to the selected sponsor&apos;s funnel
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {/* Success flash */}
                    {successCount > 0 && (
                        <Alert icon={<CheckCircleOutlineIcon />} severity="success" sx={{ mb: 2 }}>
                            <strong>{lastProspectName}</strong> added to{' '}
                            <strong>{selectedSponsor?.organization_name ?? 'sponsor'}</strong>
                            &apos;s funnel.
                            {successCount > 1 && ` (${successCount} prospects added this session)`}
                        </Alert>
                    )}

                    {/* Sponsor Selector */}
                    <Box
                        sx={{
                            p: 2,
                            mb: 2,
                            border: '1px solid',
                            borderColor:
                                formik.errors.organization_uuid && formik.touched.organization_uuid
                                    ? 'error.main'
                                    : 'primary.light',
                            borderRadius: 2,
                            backgroundColor: 'primary.50',
                        }}
                    >
                        <Typography
                            variant="caption"
                            fontWeight={700}
                            color="primary.main"
                            display="block"
                            mb={1}
                        >
                            ASSIGN TO SPONSOR
                        </Typography>
                        <Autocomplete
                            options={sponsors}
                            getOptionLabel={(o) => o.organization_name}
                            value={selectedSponsor}
                            onChange={(_, val) =>
                                formik.setFieldValue(
                                    'organization_uuid',
                                    val?.organization_uuid ?? '',
                                )
                            }
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                            {option.organization_name}
                                        </Typography>
                                        <Box display="flex" gap={1} mt={0.25}>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.active_prospects} active
                                            </Typography>
                                            <Typography variant="caption" color="success.main">
                                                {option.closed_count} closed
                                            </Typography>
                                            {option.pipeline_value > 0 && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    ${(option.pipeline_value / 1000).toFixed(0)}k
                                                    pipeline
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search and select a sponsor org..."
                                    size="small"
                                    error={
                                        formik.touched.organization_uuid &&
                                        Boolean(formik.errors.organization_uuid)
                                    }
                                    helperText={
                                        formik.touched.organization_uuid &&
                                        formik.errors.organization_uuid
                                    }
                                />
                            )}
                        />
                        {selectedSponsor && (
                            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                                <Chip
                                    label={`${selectedSponsor.active_prospects} active prospects`}
                                    size="small"
                                    variant="outlined"
                                />
                                <Chip
                                    label={`${selectedSponsor.closed_count} closed`}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                                <Chip
                                    label={`${selectedSponsor.conversion_rate}% conversion`}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        )}
                    </Box>

                    <Divider sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            PROSPECT DETAILS
                        </Typography>
                    </Divider>

                    <Grid container spacing={2}>
                        {/* Prospect Name — full width, focused first */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Prospect / Facility Name"
                                name="prospect_name"
                                inputRef={prospectNameRef}
                                value={formik.values.prospect_name}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.prospect_name &&
                                    Boolean(formik.errors.prospect_name)
                                }
                                helperText={
                                    formik.touched.prospect_name && formik.errors.prospect_name
                                }
                                required
                                size="small"
                                autoFocus={!defaultOrgUuid}
                            />
                        </Grid>

                        {/* Contact row */}
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Contact Name"
                                name="contact_name"
                                value={formik.values.contact_name}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Contact Email"
                                name="contact_email"
                                value={formik.values.contact_email}
                                onChange={formik.handleChange}
                                error={
                                    formik.touched.contact_email &&
                                    Boolean(formik.errors.contact_email)
                                }
                                helperText={
                                    formik.touched.contact_email && formik.errors.contact_email
                                }
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Contact Phone"
                                name="contact_phone"
                                value={formik.values.contact_phone}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>

                        {/* Facility row */}
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                select
                                label="Facility Type"
                                name="facility_type"
                                value={formik.values.facility_type}
                                onChange={formik.handleChange}
                                size="small"
                            >
                                <MenuItem value="">None</MenuItem>
                                {FACILITY_TYPES.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Facility Size"
                                name="facility_size"
                                value={formik.values.facility_size}
                                onChange={formik.handleChange}
                                placeholder="e.g. 45,000 sq ft"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Athlete Count"
                                name="athlete_count"
                                type="number"
                                value={formik.values.athlete_count ?? ''}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0 }}
                            />
                        </Grid>

                        {/* Athlete profile + estimated value */}
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                label="Athlete Profile"
                                name="athlete_profile"
                                value={formik.values.athlete_profile}
                                onChange={formik.handleChange}
                                placeholder="e.g., D1 Baseball, High School Football"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Estimated Deal Value"
                                name="estimated_value"
                                type="number"
                                value={formik.values.estimated_value ?? ''}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0, step: 1000 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* Identifier notes */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Identifier Notes"
                                name="notes"
                                value={formik.values.notes}
                                onChange={formik.handleChange}
                                placeholder="How was this prospect identified? Any context for the sponsor..."
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={handleClose} color="inherit">
                        Done
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    {successCount > 0 && (
                        <Typography variant="caption" color="success.main" sx={{ mr: 1 }}>
                            {successCount} added this session
                        </Typography>
                    )}
                    <Button
                        type="submit"
                        variant="outlined"
                        disabled={formik.isSubmitting}
                        onClick={() => formik.submitForm()}
                    >
                        {formik.isSubmitting ? 'Saving...' : 'Save & Add Another'}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={formik.isSubmitting}
                        onClick={async () => {
                            await formik.submitForm();
                            if (formik.isValid) handleClose();
                        }}
                    >
                        Save & Close
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AdminAddProspectDialog;
